import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { rupiah } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Printer, Download } from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const todayWib = () => new Date(Date.now() + 7 * 3600e3).toISOString().slice(0, 10);
const fmtWaktu = (iso) => new Date(iso).toLocaleString("id-ID", { timeZone: "Asia/Jakarta", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function TransactionReport() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [start, setStart] = useState(todayWib());
  const [end, setEnd] = useState(todayWib());
  const [rep, setRep] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    api.get("/admin/transactions-report", { params: { start, end, branch_id: branchId } })
      .then((r) => setRep(r.data))
      .catch((e) => { setRep(null); if (e?.response?.status !== 400) toast.error(e?.response?.data?.detail || "Gagal memuat laporan"); })
      .finally(() => setLoading(false));
  }, [start, end, branchId]);

  const branchLabel = branchId ? (branches.find((b) => b.id === branchId)?.name || "") : "Semua Cabang";

  const printTrx = () => {
    document.body.classList.add("printing-trx");
    window.print();
    setTimeout(() => document.body.classList.remove("printing-trx"), 500);
  };

  const downloadPdf = () => {
    if (!rep) return;
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("FASMART — Detail Transaksi Kasir", 14, 15);
    doc.setFontSize(9);
    doc.text(`Periode: ${start} s/d ${end}  ·  Cabang: ${branchLabel}`, 14, 21);
    autoTable(doc, {
      startY: 26,
      head: [["Waktu", "Kasir", "Item", "Bayar", "Modal", "Total", "Profit"]],
      body: rep.transactions.map((t) => [
        fmtWaktu(t.created_at),
        t.cashier_email.split("@")[0],
        t.items.map((i) => `${i.name.slice(0, 20)} x${i.qty}`).join(", "),
        t.payment_method,
        rupiah(t.modal),
        rupiah(t.total),
        rupiah(t.profit),
      ]),
      foot: [["TOTAL", "", `${rep.grand.count} struk`, "", rupiah(rep.grand.modal), rupiah(rep.grand.revenue), rupiah(rep.grand.profit)]],
      styles: { fontSize: 7 },
      headStyles: { fillColor: [5, 122, 85] },
      footStyles: { fillColor: [240, 240, 235], textColor: [20, 20, 20], fontStyle: "bold" },
    });
    if (rep.top_items?.length) {
      const y = (doc.lastAutoTable?.finalY || 26) + 8;
      doc.setFontSize(11);
      doc.text("Item Terlaris (periode ini)", 14, y);
      autoTable(doc, {
        startY: y + 3,
        head: [["Produk", "Qty", "Omzet", "Modal", "Profit"]],
        body: rep.top_items.map((i) => [i.name.slice(0, 20), String(i.qty), rupiah(i.revenue), rupiah(i.modal), rupiah(i.profit)]),
        styles: { fontSize: 7 },
        headStyles: { fillColor: [120, 90, 20] },
      });
    }
    doc.save(`laporan-transaksi_${start}_sd_${end}.pdf`);
    toast.success("PDF laporan diunduh");
  };

  return (
    <div className="space-y-4" data-testid="trx-report">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h4 className="font-display font-bold text-lg">🧾 Detail Transaksi Kasir</h4>
          <p className="text-xs text-stone-500">Pilih range tanggal bebas — tiap struk tampil dengan modal & profit. Bisa dicetak atau diunduh PDF.</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <Input data-testid="trx-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-36" />
          <span className="text-xs text-stone-400 font-bold">s/d</span>
          <Input data-testid="trx-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-36" />
          {!user.branch_id && (
            <select data-testid="trx-branch" value={branchId} onChange={(e) => setBranchId(e.target.value)} className="text-sm border border-[#E5DECF] rounded-full px-3 py-2 bg-white">
              <option value="">Semua Cabang</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          {rep && (
            <>
              <button data-testid="trx-print-btn" onClick={printTrx} className="px-4 py-2 rounded-full bg-stone-900 text-white text-sm font-bold flex items-center gap-1.5 hover:bg-emerald-700 transition"><Printer className="w-4 h-4" /> Cetak</button>
              <button data-testid="trx-pdf-btn" onClick={downloadPdf} className="px-4 py-2 rounded-full bg-emerald-700 text-white text-sm font-bold flex items-center gap-1.5 hover:bg-emerald-800 transition"><Download className="w-4 h-4" /> PDF</button>
            </>
          )}
        </div>
      </div>

      {loading && <p className="text-sm text-stone-400">Memuat…</p>}
      {rep && (
        <div id="trx-report-print" data-testid="trx-report-print">
          <div className="print-only" style={{ marginBottom: 12 }}>
            <div style={{ fontWeight: 900, fontSize: 18 }}>FASMART — Detail Transaksi Kasir</div>
            <div style={{ fontSize: 12 }}>Periode: {start} s/d {end} · Cabang: {branchLabel}</div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white rounded-2xl border border-[#E5DECF] p-4"><div className="text-xs text-stone-500 font-bold">Transaksi</div><div className="font-display font-black text-xl" data-testid="trx-grand-count">{rep.grand.count} struk</div></div>
            <div className="bg-white rounded-2xl border border-[#E5DECF] p-4"><div className="text-xs text-stone-500 font-bold">Omzet</div><div className="font-display font-black text-xl" data-testid="trx-grand-revenue">{rupiah(rep.grand.revenue)}</div></div>
            <div className="bg-white rounded-2xl border border-[#E5DECF] p-4"><div className="text-xs text-stone-500 font-bold">Total Modal</div><div className="font-display font-black text-xl text-stone-600" data-testid="trx-grand-modal">{rupiah(rep.grand.modal)}</div></div>
            <div className="bg-white rounded-2xl border-2 border-emerald-200 p-4"><div className="text-xs text-emerald-700 font-bold">Profit Total</div><div className="font-display font-black text-xl text-emerald-700" data-testid="trx-grand-profit">{rupiah(rep.grand.profit)}</div></div>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5DECF] overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]"><th className="p-3">Waktu</th><th className="p-3">Kasir</th><th className="p-3">Item</th><th className="p-3">Bayar</th><th className="p-3 text-right">Modal</th><th className="p-3 text-right">Total</th><th className="p-3 text-right">Profit</th></tr></thead>
              <tbody>
                {rep.transactions.map((t) => (
                  <tr key={t.id} className="border-b border-stone-100 last:border-0 align-top" data-testid={`trx-row-${t.id}`}>
                    <td className="p-3 whitespace-nowrap text-xs">{fmtWaktu(t.created_at)}</td>
                    <td className="p-3 text-xs font-semibold">{t.cashier_email.split("@")[0]}</td>
                    <td className="p-3 text-xs">{t.items.map((i) => `${i.name.length > 20 ? i.name.slice(0, 20) : i.name} x${i.qty}`).join(", ") || "—"}</td>
                    <td className="p-3 text-xs uppercase">{t.payment_method}</td>
                    <td className="p-3 text-right text-xs text-stone-500">{rupiah(t.modal)}</td>
                    <td className="p-3 text-right text-xs font-bold">{rupiah(t.total)}</td>
                    <td className={`p-3 text-right text-xs font-bold ${t.profit >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{rupiah(t.profit)}</td>
                  </tr>
                ))}
                {!rep.transactions.length && <tr><td colSpan={7} className="p-4 text-stone-400 text-xs">Tidak ada transaksi pada range tanggal ini.</td></tr>}
              </tbody>
              {rep.transactions.length > 0 && (
                <tfoot><tr className="border-t-2 border-stone-900 font-black text-xs"><td className="p-3" colSpan={4}>TOTAL ({rep.grand.count} struk)</td><td className="p-3 text-right">{rupiah(rep.grand.modal)}</td><td className="p-3 text-right">{rupiah(rep.grand.revenue)}</td><td className="p-3 text-right text-emerald-700">{rupiah(rep.grand.profit)}</td></tr></tfoot>
              )}
            </table>
          </div>
          {rep.top_items?.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E5DECF] overflow-x-auto mt-3" data-testid="trx-top-items">
              <div className="p-3 pb-0 font-display font-bold">🏆 Item Terlaris (periode ini)</div>
              <table className="w-full text-sm mt-1">
                <thead><tr className="text-left text-[10px] font-mono uppercase tracking-widest text-stone-500 border-b border-[#E5DECF]"><th className="p-3">Produk</th><th className="p-3 text-right">Qty</th><th className="p-3 text-right">Omzet</th><th className="p-3 text-right">Modal</th><th className="p-3 text-right">Profit</th></tr></thead>
                <tbody>
                  {rep.top_items.map((i, idx) => (
                    <tr key={idx} className="border-b border-stone-100 last:border-0" data-testid={`trx-top-item-${idx}`}>
                      <td className="p-3 text-xs font-semibold">{i.name.length > 20 ? i.name.slice(0, 20) : i.name}</td>
                      <td className="p-3 text-right text-xs font-bold">{i.qty}</td>
                      <td className="p-3 text-right text-xs">{rupiah(i.revenue)}</td>
                      <td className="p-3 text-right text-xs text-stone-500">{rupiah(i.modal)}</td>
                      <td className={`p-3 text-right text-xs font-bold ${i.profit >= 0 ? "text-emerald-700" : "text-rose-600"}`}>{rupiah(i.profit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
